/** /route_stops/ — cross-tenant bus-stop catalog (Vehicle_Route_Stop).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Stops are licensee-scoped `VersionMixin` resources with
 * an `updated_ts` — writes carry an optimistic-concurrency token (409
 * `route_stop_modified` on drift, `route_stop_in_use` on a blocked delete).
 *
 * The image is a tri-state on write: a multipart `image` part replaces it,
 * `image_id: null` clears it, and omitting both keeps the current one. The
 * `attractions[]` are ATTRACTION-type Activities (`/activities/?type=5`) with a
 * per-row distance (metres) — destructive-replace on write.
 */
import { apiDelete, apiGet, client } from './client';
import type { ImageChunks } from '@/utils/imageUrl';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** An attraction placed on a stop. `attraction_id` is an ATTRACTION-type
 *  Activity; `distance` is metres from the stop. */
export interface RouteStopAttraction {
  attraction_id: number;
  name: string | null;
  sequence: number;
  distance: number;
}

export interface RouteStopListItem {
  id: number;
  tenant: TenantRef;
  name: string;
  label: string | null;
  latitude: number | null;
  longitude: number | null;
  enabled: boolean;
  has_image: boolean;
  attraction_count: number;
  /** On-stop warning banner (visible column). */
  warning: string | null;
  /** Longer description (hidden by default). */
  description: string | null;
  updated_ts: string | null;
}

export interface RouteStopDetail extends RouteStopListItem {
  image: ImageChunks | null;
  tags: NamedRef[];
  attractions: RouteStopAttraction[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/** The `_json` metadata of a stop write (also the whole body of a JSON-only
 *  PATCH). `tour_operator` + `name` are required on create. `image_id: null`
 *  clears the image; a multipart `image` part replaces it. */
export interface RouteStopWriteJson {
  tour_operator?: number;
  name?: string;
  label?: string | null;
  description?: string | null;
  warning?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  enabled?: boolean;
  /** null clears the current image (a multipart file part replaces it). */
  image_id?: number | null;
  tags?: { id: number }[];
  attractions?: { attraction_id: number; sequence: number; distance: number }[];
  expected_updated_ts?: string | null;
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<RouteStopListItem>> {
  return apiGet<PaginatedResponse<RouteStopListItem>>(`/route_stops/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<RouteStopDetail> {
  return apiGet<RouteStopDetail>(`/route_stops/${id}/`);
}

/** POST /route_stops/ — multipart (`_json` + `image` file) when a File is
 *  staged, else plain JSON. */
export async function create(body: RouteStopWriteJson, image?: File | null): Promise<RouteStopDetail> {
  if (image) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('image', image);
    const r = await client.post<RouteStopDetail>('/route_stops/', fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.post<RouteStopDetail>('/route_stops/', body);
  return r.data;
}

/** PATCH /route_stops/{id}/ — multipart when a File is staged (swap the image);
 *  plain JSON otherwise. To clear the current image, put `image_id: null` in
 *  `body` and pass no File. */
export async function update(
  id: number,
  body: RouteStopWriteJson,
  image?: File | null,
): Promise<RouteStopDetail> {
  if (image) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('image', image);
    const r = await client.patch<RouteStopDetail>(`/route_stops/${id}/`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.patch<RouteStopDetail>(`/route_stops/${id}/`, body);
  return r.data;
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/route_stops/${id}/`);
}
